import { useEffect, useState } from 'react';
import { fetchPackagePerks } from '../api/store.js';
import { useStoreUI } from '../context/StoreUIContext.jsx';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { SafeFormattedText } from './SafeFormattedText.jsx';
import { SafeTebexHtml } from './SafeTebexHtml.jsx';

export function PerksModal() {
  const { perksPackageId, closePerks } = useStoreUI();
  const [state, setState] = useState({ status: 'idle', data: null });

  const isOpen = perksPackageId !== null;

  useEscapeKey(closePerks, isOpen);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    setState({ status: 'loading', data: null });

    fetchPackagePerks(perksPackageId).then((data) => {
      if (cancelled) return;

      setState(data?.error ? { status: 'error', data: null } : { status: 'ready', data });
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, perksPackageId]);

  useEffect(() => {
    document.body.classList.toggle('perks-modal-open', isOpen);

    return () => document.body.classList.remove('perks-modal-open');
  }, [isOpen]);

  if (!isOpen) return null;

  const packages = state.data?.packages ?? [];
  const perks = state.data?.perks ?? [];
  const mode = state.data?.mode ?? 'all';

  const infoMode = state.data?.info_mode ?? 'perks';
  const selected = packages.find((pkg) => pkg.is_selected) ?? packages[0];

  const heading = headingFor({ infoMode, mode, selected, data: state.data });

  return (
    <div
      className="perks-modal-overlay"
      onClick={(event) => event.target === event.currentTarget && closePerks()}
    >
      <div className={`perks-modal-box${infoMode === 'tebex' ? ' perks-modal-box-tebex' : ''}`}>
        <div className="perks-modal-header">
          <h3>{state.status === 'loading' ? 'Loading…' : heading}</h3>
          <button
            type="button"
            className="perks-modal-close"
            aria-label="Close"
            onClick={closePerks}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="perks-modal-body">
          <InfoBody
            status={state.status}
            infoMode={infoMode}
            text={state.data?.text}
            html={state.data?.html}
            packages={packages}
            perks={perks}
          />
        </div>
      </div>
    </div>
  );
}


function headingFor({ infoMode, mode, selected, data }) {
  if (infoMode === 'text' || infoMode === 'tebex') return data?.title || 'Package Info';

  if (infoMode === 'none') return 'Package Info';
  if (mode === 'all') return 'All Ranks & Perks';

  return selected ? `${selected.name} — Rank Comparison` : 'Rank Comparison';
}

function InfoBody({ status, infoMode, text, html, packages, perks }) {
  if (status === 'loading') {
    return (
      <div className="perks-loading">
        <i className="fa-solid fa-spinner fa-spin" />
      </div>
    );
  }

  if (status === 'error') {
    return <p className="perks-empty">Failed to load this package's info.</p>;
  }

  if (infoMode === 'none') {
    return <p className="perks-empty">No information available for this package.</p>;
  }

  if (infoMode === 'text') {
    return <SafeFormattedText className="perks-text" text={text} />;
  }

  if (infoMode === 'tebex') {
    return <SafeTebexHtml className="perks-text perks-tebex-html" html={html} />;
  }

  if (packages.length === 0) {
    return <p className="perks-empty">No comparison data available yet.</p>;
  }

  if (perks.length === 0) {
    return (
      <p className="perks-empty">No permissions have been configured for this rank category yet.</p>
    );
  }

  return (
    <div className="perks-table-wrap">
      <table className="perks-compare-table">
        <thead>
          <tr>
            <th className="perks-perm-col">Permission</th>
            {packages.map((pkg) => (
              <th key={pkg.id} className={`perks-rank-col${columnClass(pkg)}`}>
                {pkg.name}
                {pkg.is_selected && <span className="perks-badge perks-badge-you">you</span>}
                {pkg.is_target && (
                  <span className="perks-badge perks-badge-target">this upgrade</span>
                )}
                {pkg.is_prev && <span className="perks-badge perks-badge-prev">prev rank</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {perks.map((perk) => (
            <tr key={perk.id}>
              <td className="perks-perm-name">{perk.name}</td>
              {packages.map((pkg) => (
                <td key={pkg.id} className={`perks-cell${columnClass(pkg)}`}>
                  <PerkValue perk={perk} cell={perk.values[pkg.id]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PerkValue({ perk, cell }) {
  if (perk.amount_based) {
    return cell?.value ? <span className="perks-val">{cell.value}</span> : <NoPerk />;
  }

  return cell?.has ? (
    <span className="perks-yes">
      <i className="fa-solid fa-check" />
    </span>
  ) : (
    <NoPerk />
  );
}

function NoPerk() {
  return (
    <span className="perks-no">
      <i className="fa-solid fa-minus" />
    </span>
  );
}

function columnClass(pkg) {
  if (pkg.is_target) return ' perks-col-target';
  if (pkg.is_selected) return ' perks-col-selected';
  if (pkg.is_prev) return ' perks-col-prev';
  return '';
}
